// frontend/src/pages/Recepcion/ConfiguracionPantallaInformativaPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import {
    obtenerConfiguracionPantalla,
    guardarConfiguracionPantalla,
    regenerarClavePantalla,
    obtenerEstadoTv,
    enviarControlPantalla,
    linkPantalla,
} from '../../services/pantallaInformativaService';
import { analizarUrlYoutube, miniaturaYoutube } from '../../utils/youtube';
import '../../style/pages/HomePage.css';
import '../../style/pages/ConfiguracionPantallaInformativa.css';

const LARGO_MAXIMO_MENSAJE = 300;
// Mismo límite que PantallaInformativaService.MAXIMO_VIDEOS.
const MAXIMO_VIDEOS = 30;
// Cada cuánto se pregunta qué está pasando el TV. Después de mandarle una orden se pregunta más
// seguido, hasta que el TV la confirma (late cada segundo y avisa apenas la aplica).
const INTERVALO_ESTADO_TV_MS = 3000;
const INTERVALO_ESTADO_TV_RAPIDO_MS = 500;
const MS_SEGUIMIENTO_ORDEN = 10000;
// Con nombre fijo, "Abrir pantalla" reutiliza la ventana del TV si ya está abierta en vez de
// abrir otra encima.
const NOMBRE_VENTANA_TV = 'pantalla-informativa';
// La vista previa dibuja el TV en su tamaño real y lo achica: así se ve exactamente lo mismo.
const ANCHO_TV = 1920;
const ALTO_TV = 1080;

const MODOS = [
    {
        valor: 'MIXTO',
        titulo: 'Mixto',
        descripcion: 'Los turnos en espera, el video y el reloj.',
        icono: 'bi-layout-split',
        predeterminado: true,
    },
    {
        valor: 'SOLO_VIDEO',
        titulo: 'Solo video',
        descripcion: 'El video a toda la pantalla, con el aviso al pie.',
        icono: 'bi-play-btn',
    },
    {
        valor: 'SOLO_TURNERO',
        titulo: 'Solo turnero',
        descripcion: 'Las dos colas de turnos, sin video.',
        icono: 'bi-list-ol',
    },
];

const TEXTO_ESTADO_TV = {
    REPRODUCIENDO: 'Reproduciendo',
    PAUSADO: 'En pausa',
    ERROR: 'YouTube no pudo pasar ningún video; reintenta solo en un minuto',
    SIN_VIDEO: 'Sin video',
};

function mensajeDeError(e, porDefecto) {
    return e?.response?.data?.message || e?.response?.data?.mensaje || porDefecto;
}

/** La cola tal como viaja al backend, para comparar si hubo cambios. */
function firmaCola(videos) {
    return videos.map((video) => `${video.id ?? ''}|${video.url}`).join('\n');
}

/** El TV achicado, en vivo. Sin barra de control y siempre en silencio (ver PantallaInformativaPage). */
function VistaPreviaTv({ clave, version }) {
    const marcoRef = useRef(null);
    const [escala, setEscala] = useState(0);

    useEffect(() => {
        const marco = marcoRef.current;
        if (!marco) return undefined;
        const medir = () => setEscala(marco.clientWidth / ANCHO_TV);
        medir();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observador = new ResizeObserver(medir);
        observador.observe(marco);
        return () => observador.disconnect();
    }, []);

    return (
        <div className="cpi-vista-previa" ref={marcoRef}>
            {clave && escala > 0 && (
                <iframe
                    key={`${clave}-${version}`}
                    src={`/tv-screen#clave=${encodeURIComponent(clave)}&vistaPrevia=1`}
                    title="Vista previa de la pantalla informativa"
                    allow="autoplay; encrypted-media"
                    className="cpi-vista-previa__tv"
                    style={{ width: ANCHO_TV, height: ALTO_TV, transform: `scale(${escala})` }}
                    tabIndex={-1}
                />
            )}
        </div>
    );
}

/**
 * Configuración de la pantalla informativa (TV de la sala de espera): qué se muestra (modo), la
 * cola de videos, el aviso del pie y el link con el que se abre el TV. Tiene además el control
 * remoto del video: muestra qué está pasando el TV y deja pausarlo o pasar a otro. Se entra desde
 * Atención.
 */
const ConfiguracionPantallaInformativaPage = () => {
    const navigate = useNavigate();
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');
    const [exito, setExito] = useState('');
    const [guardado, setGuardado] = useState({ videos: [], videoConSonido: false, mensaje: '', modo: 'MIXTO' });
    // Cada video lleva una clave local para React: los nuevos todavía no tienen id.
    const [videos, setVideos] = useState([]);
    const [nuevoVideo, setNuevoVideo] = useState('');
    const [videoConSonido, setVideoConSonido] = useState(false);
    const [mensaje, setMensaje] = useState('');
    const [modo, setModo] = useState('MIXTO');
    const [clave, setClave] = useState('');
    const [confirmandoClaveNueva, setConfirmandoClaveNueva] = useState(false);
    const [copiado, setCopiado] = useState(false);
    // Se incrementa al guardar para recargar la vista previa sin esperar a que el TV consulte solo.
    const [versionVistaPrevia, setVersionVistaPrevia] = useState(0);
    const [estadoTv, setEstadoTv] = useState(null);
    const [enviandoControl, setEnviandoControl] = useState(false);
    // Los títulos que fue informando el TV, para reconocer cada video de la cola.
    const [titulos, setTitulos] = useState({});
    const contadorNuevos = useRef(0);
    const seguirOrdenHastaRef = useRef(0);
    const consultarEstadoTvRef = useRef(null);

    const aplicarConfiguracion = useCallback((config) => {
        const valores = {
            videos: (config?.videos || []).map((video) => ({ id: video.id, url: video.url })),
            videoConSonido: Boolean(config?.videoConSonido),
            mensaje: config?.mensaje || '',
            modo: config?.modo || 'MIXTO',
        };
        setGuardado(valores);
        setVideos(valores.videos.map((video) => ({ ...video, clave: `id-${video.id}` })));
        setVideoConSonido(valores.videoConSonido);
        setMensaje(valores.mensaje);
        setModo(valores.modo);
        if (config?.clave) setClave(config.clave);
    }, []);

    useEffect(() => {
        let activo = true;
        obtenerConfiguracionPantalla()
            .then((config) => {
                if (activo) aplicarConfiguracion(config);
            })
            .catch((e) => {
                if (activo) setError(mensajeDeError(e, 'No se pudo cargar la configuración de la pantalla.'));
            })
            .finally(() => {
                if (activo) setCargando(false);
            });
        return () => {
            activo = false;
        };
    }, [aplicarConfiguracion]);

    // Qué está pasando el TV, mientras esta página está abierta. Cada consulta nueva descarta la
    // respuesta de la anterior si todavía no volvió: una respuesta vieja no pisa una orden recién dada.
    useEffect(() => {
        let activo = true;
        let temporizador = null;
        let vuelta = 0;
        const consultar = async () => {
            clearTimeout(temporizador);
            const estaVuelta = ++vuelta;
            try {
                const estado = await obtenerEstadoTv();
                if (activo && estaVuelta === vuelta) setEstadoTv(estado);
            } catch {
                // Se vuelve a consultar en unos segundos.
            }
            if (!activo || estaVuelta !== vuelta) return;
            const rapido = Date.now() < seguirOrdenHastaRef.current;
            temporizador = setTimeout(consultar, rapido ? INTERVALO_ESTADO_TV_RAPIDO_MS : INTERVALO_ESTADO_TV_MS);
        };
        consultarEstadoTvRef.current = consultar;
        consultar();
        return () => {
            activo = false;
            consultarEstadoTvRef.current = null;
            clearTimeout(temporizador);
        };
    }, []);

    useEffect(() => {
        const id = estadoTv?.idVideo;
        const titulo = estadoTv?.titulo;
        if (id == null || !titulo) return;
        setTitulos((anteriores) => (anteriores[id] === titulo ? anteriores : { ...anteriores, [id]: titulo }));
    }, [estadoTv?.idVideo, estadoTv?.titulo]);

    const nuevoAnalizado = useMemo(() => analizarUrlYoutube(nuevoVideo), [nuevoVideo]);
    const nuevoInvalido = nuevoVideo.trim() !== '' && !nuevoAnalizado;
    const colaLlena = videos.length >= MAXIMO_VIDEOS;
    // Un link válido escrito pero sin "Agregar" también se guarda: que no se pierda sin aviso.
    const nuevoPendiente = Boolean(nuevoAnalizado) && !colaLlena;
    const hayCambios = firmaCola(videos) !== firmaCola(guardado.videos)
        || nuevoPendiente
        || videoConSonido !== guardado.videoConSonido
        || mensaje.trim() !== guardado.mensaje
        || modo !== guardado.modo;
    const link = clave ? linkPantalla(clave) : '';

    const agregarVideo = () => {
        if (!nuevoAnalizado || colaLlena) return;
        contadorNuevos.current += 1;
        setVideos((lista) => [...lista, { id: null, url: nuevoVideo.trim(), clave: `nuevo-${contadorNuevos.current}` }]);
        setNuevoVideo('');
    };

    const moverVideo = (indice, desplazamiento) => {
        setVideos((lista) => {
            const destino = indice + desplazamiento;
            if (destino < 0 || destino >= lista.length) return lista;
            const copia = [...lista];
            [copia[indice], copia[destino]] = [copia[destino], copia[indice]];
            return copia;
        });
    };

    const quitarVideo = (claveVideo) => {
        setVideos((lista) => lista.filter((video) => video.clave !== claveVideo));
    };

    const guardar = async () => {
        if (nuevoInvalido || guardando || !hayCambios) return;
        setGuardando(true);
        setError('');
        setExito('');
        const cola = nuevoPendiente ? [...videos, { id: null, url: nuevoVideo.trim() }] : videos;
        try {
            const config = await guardarConfiguracionPantalla({
                videos: cola.map((video) => ({ id: video.id ?? null, url: video.url })),
                videoConSonido,
                mensaje: mensaje.trim(),
                modo,
            });
            aplicarConfiguracion(config);
            setNuevoVideo('');
            setVersionVistaPrevia((v) => v + 1);
            setExito('Cambios guardados. El TV los toma en unos segundos.');
        } catch (e) {
            setError(mensajeDeError(e, 'No se pudo guardar la configuración.'));
        } finally {
            setGuardando(false);
        }
    };

    const descartarCambios = () => {
        setVideos(guardado.videos.map((video) => ({ ...video, clave: `id-${video.id}` })));
        setNuevoVideo('');
        setVideoConSonido(guardado.videoConSonido);
        setMensaje(guardado.mensaje);
        setModo(guardado.modo);
        setError('');
    };

    const abrirPantalla = () => {
        if (!link) return;
        window.open(link, NOMBRE_VENTANA_TV, 'popup,width=1280,height=720');
    };

    const copiarLink = async () => {
        if (!link || !navigator.clipboard) return;
        try {
            await navigator.clipboard.writeText(link);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        } catch {
            setError('No se pudo copiar el link.');
        }
    };

    const generarClaveNueva = async () => {
        setGuardando(true);
        setError('');
        setExito('');
        try {
            const config = await regenerarClavePantalla();
            setClave(config?.clave || '');
            setConfirmandoClaveNueva(false);
            setExito('Link nuevo generado. El link anterior ya no muestra la pantalla.');
        } catch (e) {
            setError(mensajeDeError(e, 'No se pudo generar el link nuevo.'));
        } finally {
            setGuardando(false);
        }
    };

    // ── Control remoto ──────────────────────────────────────────────────────────────────────

    const tvConectado = Boolean(estadoTv?.conectado);
    const ultimaOrden = estadoTv?.control;
    // La última orden todavía no llegó al TV (tarda hasta un segundo): se muestra como si ya hubiera
    // llegado, así el botón no vuelve atrás mientras tanto.
    const ordenEnCamino = tvConectado && estadoTv?.secuenciaAplicada != null
        && (ultimaOrden?.secuencia ?? 0) > estadoTv.secuenciaAplicada;
    // Pausar o Reanudar según lo que informa el TV; sin TV o con la orden en camino, según la orden.
    const tvEnPausa = tvConectado && !ordenEnCamino
        ? estadoTv?.estado === 'PAUSADO'
        : Boolean(ultimaOrden?.pausado);
    const puedeControlar = guardado.videos.length > 0 && guardado.modo !== 'SOLO_TURNERO' && !enviandoControl;
    const indiceEnTv = tvConectado ? guardado.videos.findIndex((video) => video.id === estadoTv?.idVideo) : -1;
    const tituloEnTv = estadoTv?.titulo || (estadoTv?.idVideo != null ? titulos[estadoTv.idVideo] : '');

    const controlar = async (accion, idVideo = null) => {
        setEnviandoControl(true);
        setError('');
        try {
            const control = await enviarControlPantalla(accion, idVideo);
            // Queda "en camino" hasta que el TV avise que la aplicó; mientras, se pregunta seguido.
            setEstadoTv((anterior) => (anterior ? { ...anterior, control } : anterior));
            seguirOrdenHastaRef.current = Date.now() + MS_SEGUIMIENTO_ORDEN;
            consultarEstadoTvRef.current?.();
        } catch (e) {
            setError(mensajeDeError(e, 'No se pudo enviar la orden al TV.'));
        } finally {
            setEnviandoControl(false);
        }
    };

    let textoControl;
    if (!tvConectado) {
        textoControl = 'El TV no está conectado. Abrilo con "Abrir pantalla" (más abajo).';
    } else if (estadoTv?.estado === 'SIN_VIDEO' || estadoTv?.idVideo == null) {
        textoControl = guardado.modo === 'SOLO_TURNERO'
            ? 'El TV está en modo Solo turnero: no pasa videos.'
            : 'El TV no tiene videos para pasar.';
    } else {
        textoControl = null;
    }

    return (
        <div className="homepage-container">
            <div className="homepage-header rec-header">
                <h1 className="homepage-title">
                    <i className="bi bi-tv me-3"></i>
                    Pantalla Informativa
                </h1>
                <p className="homepage-subtitle">
                    Lo que se ve en el TV de la sala de espera: las colas de turnos, los videos y el aviso del pie.
                </p>
            </div>

            <div className="cpi-acciones">
                <button type="button" className="rec-btn-secondary" onClick={() => navigate('/recepcion/atencion')}>
                    <i className="bi bi-arrow-left me-2"></i>
                    Volver a Atención
                </button>
            </div>

            {error && <div className="alert alert-danger py-2">{error}</div>}
            {exito && <div className="alert alert-success py-2">{exito}</div>}

            {cargando ? (
                <div className="eventos-section rec-panel text-center">
                    <span className="spinner-border spinner-border-sm me-2" />
                    Cargando configuración…
                </div>
            ) : (
                <div className="cpi-grilla">
                    <section className="eventos-section rec-panel cpi-seccion">
                        <h2 className="eventos-section-title cpi-titulo">
                            <i className="bi bi-sliders me-2"></i>
                            Contenido
                        </h2>

                        <fieldset className="mb-4">
                            <legend className="form-label fw-semibold cpi-leyenda">
                                <i className="bi bi-grid-1x2 me-2"></i>Qué muestra el TV
                            </legend>
                            <div className="cpi-modos">
                                {MODOS.map((opcion) => (
                                    <label
                                        key={opcion.valor}
                                        className={`cpi-modo${modo === opcion.valor ? ' cpi-modo--activo' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name="pantalla-modo"
                                            className="visually-hidden"
                                            value={opcion.valor}
                                            checked={modo === opcion.valor}
                                            onChange={() => setModo(opcion.valor)}
                                            disabled={guardando}
                                        />
                                        <i className={`bi ${opcion.icono} cpi-modo__icono`} aria-hidden="true" />
                                        <span className="cpi-modo__titulo">
                                            {opcion.titulo}
                                            {opcion.predeterminado && (
                                                <span className="cpi-modo__etiqueta">Predeterminado</span>
                                            )}
                                        </span>
                                        <span className="cpi-modo__descripcion">{opcion.descripcion}</span>
                                    </label>
                                ))}
                            </div>
                        </fieldset>

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-semibold" htmlFor="pantalla-nuevo-video">
                                <i className="bi bi-youtube me-2"></i>Videos de YouTube
                            </Form.Label>
                            {modo === 'SOLO_TURNERO' && (
                                <div className="cpi-nota cpi-nota--aviso mb-2">
                                    <i className="bi bi-info-circle me-1" />
                                    En modo Solo turnero el TV no pasa videos.
                                </div>
                            )}

                            {videos.length > 0 ? (
                                <ol className="cpi-cola-videos">
                                    {videos.map((video, indice) => {
                                        const datos = analizarUrlYoutube(video.url);
                                        const miniatura = miniaturaYoutube(datos);
                                        const enTv = tvConectado && video.id != null && video.id === estadoTv?.idVideo;
                                        const titulo = (video.id != null && titulos[video.id])
                                            || (datos?.videoId ? 'Video de YouTube' : 'Lista de reproducción');
                                        return (
                                            <li
                                                key={video.clave}
                                                className={`cpi-video-item${enTv ? ' cpi-video-item--en-tv' : ''}`}
                                            >
                                                <span className="cpi-video-item__numero">{indice + 1}</span>
                                                {miniatura ? (
                                                    <img src={miniatura} alt="" className="cpi-video-item__miniatura" />
                                                ) : (
                                                    <span className="cpi-video-item__miniatura cpi-video-item__miniatura--lista">
                                                        <i className="bi bi-collection-play" aria-hidden="true" />
                                                    </span>
                                                )}
                                                <span className="cpi-video-item__datos">
                                                    <span className="cpi-video-item__titulo">
                                                        {titulo}
                                                        {enTv && <span className="cpi-video-item__en-tv">En el TV</span>}
                                                        {video.id == null && <span className="cpi-video-item__nuevo">Sin guardar</span>}
                                                    </span>
                                                    <a
                                                        href={video.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="cpi-video-item__link"
                                                    >
                                                        {video.url}
                                                    </a>
                                                </span>
                                                <span className="cpi-video-item__acciones">
                                                    {video.id != null && guardado.videos.some((g) => g.id === video.id) && (
                                                        <button
                                                            type="button"
                                                            className="cpi-icono-boton"
                                                            onClick={() => controlar('REPRODUCIR', video.id)}
                                                            disabled={!puedeControlar}
                                                            title="Pasarlo ahora en el TV"
                                                            aria-label={`Pasar el video ${indice + 1} ahora en el TV`}
                                                        >
                                                            <i className="bi bi-play-fill" aria-hidden="true" />
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="cpi-icono-boton"
                                                        onClick={() => moverVideo(indice, -1)}
                                                        disabled={guardando || indice === 0}
                                                        title="Subir"
                                                        aria-label={`Subir el video ${indice + 1}`}
                                                    >
                                                        <i className="bi bi-arrow-up" aria-hidden="true" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="cpi-icono-boton"
                                                        onClick={() => moverVideo(indice, 1)}
                                                        disabled={guardando || indice === videos.length - 1}
                                                        title="Bajar"
                                                        aria-label={`Bajar el video ${indice + 1}`}
                                                    >
                                                        <i className="bi bi-arrow-down" aria-hidden="true" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="cpi-icono-boton cpi-icono-boton--peligro"
                                                        onClick={() => quitarVideo(video.clave)}
                                                        disabled={guardando}
                                                        title="Quitar de la cola"
                                                        aria-label={`Quitar el video ${indice + 1}`}
                                                    >
                                                        <i className="bi bi-trash" aria-hidden="true" />
                                                    </button>
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ol>
                            ) : (
                                <p className="cpi-cola-vacia">
                                    No hay videos: el TV muestra el nombre del estudio.
                                </p>
                            )}

                            <div className="cpi-agregar">
                                <Form.Control
                                    id="pantalla-nuevo-video"
                                    type="url"
                                    placeholder="https://www.youtube.com/watch?v=… (video o lista de reproducción)"
                                    value={nuevoVideo}
                                    onChange={(e) => setNuevoVideo(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            agregarVideo();
                                        }
                                    }}
                                    isInvalid={nuevoInvalido}
                                    disabled={guardando || colaLlena}
                                />
                                <button
                                    type="button"
                                    className="rec-btn-secondary"
                                    onClick={agregarVideo}
                                    disabled={guardando || !nuevoAnalizado || colaLlena}
                                >
                                    <i className="bi bi-plus-lg me-1" /> Agregar
                                </button>
                                <Form.Control.Feedback type="invalid">
                                    No es un link de YouTube válido.
                                </Form.Control.Feedback>
                            </div>
                            <Form.Text muted>
                                {colaLlena
                                    ? `La cola llegó al máximo de ${MAXIMO_VIDEOS} videos.`
                                    : 'Se pasan en orden y, al terminar el último, vuelve a empezar. Si el link es de un minuto puntual, arranca desde ahí. Un video que YouTube no deja pasar fuera de su sitio se saltea.'}
                            </Form.Text>
                            <Form.Check
                                type="switch"
                                id="pantalla-video-con-sonido"
                                className="mt-2"
                                label="Reproducir con sonido"
                                checked={videoConSonido}
                                onChange={(e) => setVideoConSonido(e.target.checked)}
                                disabled={guardando}
                            />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-semibold" htmlFor="pantalla-mensaje">
                                <i className="bi bi-megaphone me-2"></i>Aviso al pie (opcional)
                            </Form.Label>
                            <Form.Control
                                id="pantalla-mensaje"
                                as="textarea"
                                rows={3}
                                maxLength={LARGO_MAXIMO_MENSAJE}
                                placeholder="Ej: Recuerde traer su DNI y la documentación del trámite."
                                value={mensaje}
                                onChange={(e) => setMensaje(e.target.value)}
                                disabled={guardando}
                            />
                            <Form.Text muted>
                                {mensaje.length}/{LARGO_MAXIMO_MENSAJE}. Pasa como cartel en movimiento al pie del TV.
                            </Form.Text>
                        </Form.Group>

                        <div className="cpi-botonera">
                            <button
                                type="button"
                                className="rec-btn-secondary"
                                onClick={descartarCambios}
                                disabled={guardando || !hayCambios}
                            >
                                Descartar cambios
                            </button>
                            <button
                                type="button"
                                className="rec-btn-primary"
                                onClick={guardar}
                                disabled={guardando || nuevoInvalido || !hayCambios}
                            >
                                {guardando
                                    ? <span className="spinner-border spinner-border-sm me-1" />
                                    : <i className="bi bi-check-lg me-1" />}
                                Guardar cambios
                            </button>
                        </div>
                    </section>

                    <div className="cpi-columna">
                        <section className="eventos-section rec-panel cpi-seccion">
                            <h2 className="eventos-section-title cpi-titulo">
                                <i className="bi bi-joystick me-2"></i>
                                Control del video
                            </h2>
                            <div className="cpi-control-tv" aria-live="polite">
                                <span
                                    className={`cpi-control-tv__punto${tvConectado ? ' cpi-control-tv__punto--conectado' : ''}`}
                                    aria-hidden="true"
                                />
                                {textoControl ? (
                                    <span>{textoControl}</span>
                                ) : (
                                    <span>
                                        En el TV ahora: <strong>{tituloEnTv || 'Video de YouTube'}</strong>
                                        {indiceEnTv !== -1 && ` · Video ${indiceEnTv + 1} de ${guardado.videos.length}`}
                                        {' · '}
                                        {ordenEnCamino
                                            ? 'Enviando la orden al TV…'
                                            : TEXTO_ESTADO_TV[estadoTv?.estado] || 'Cargando'}
                                    </span>
                                )}
                            </div>
                            <div className="cpi-botonera cpi-botonera--izquierda">
                                <button
                                    type="button"
                                    className="rec-btn-secondary"
                                    onClick={() => controlar('ANTERIOR')}
                                    disabled={!puedeControlar}
                                >
                                    <i className="bi bi-skip-start-fill me-1" /> Anterior
                                </button>
                                <button
                                    type="button"
                                    className="rec-btn-primary"
                                    onClick={() => controlar(tvEnPausa ? 'REANUDAR' : 'PAUSAR')}
                                    disabled={!puedeControlar}
                                >
                                    <i className={`bi ${tvEnPausa ? 'bi-play-fill' : 'bi-pause-fill'} me-1`} />
                                    {tvEnPausa ? 'Reanudar' : 'Pausar'}
                                </button>
                                <button
                                    type="button"
                                    className="rec-btn-secondary"
                                    onClick={() => controlar('SIGUIENTE')}
                                    disabled={!puedeControlar}
                                >
                                    Siguiente <i className="bi bi-skip-end-fill ms-1" />
                                </button>
                            </div>
                            <p className="cpi-nota">
                                En pausa, el TV tapa el video con el cartel del estudio. Actúa sobre lo guardado.
                            </p>
                        </section>

                        <section className="eventos-section rec-panel cpi-seccion">
                            <h2 className="eventos-section-title cpi-titulo">
                                <i className="bi bi-display me-2"></i>
                                Vista previa
                            </h2>
                            <VistaPreviaTv clave={clave} version={versionVistaPrevia} />
                            <p className="cpi-nota">
                                Muestra lo guardado, en vivo y sin sonido. Los cambios sin guardar no se ven
                                todavía, y el video no va sincronizado con el del TV.
                            </p>

                            <div className="cpi-botonera cpi-botonera--izquierda">
                                <button type="button" className="rec-btn-primary" onClick={abrirPantalla} disabled={!link}>
                                    <i className="bi bi-box-arrow-up-right me-1" /> Abrir pantalla
                                </button>
                                <button type="button" className="rec-btn-secondary" onClick={copiarLink} disabled={!link}>
                                    <i className={`bi ${copiado ? 'bi-check2' : 'bi-clipboard'} me-1`} />
                                    {copiado ? 'Copiado' : 'Copiar link'}
                                </button>
                                {!confirmandoClaveNueva ? (
                                    <button
                                        type="button"
                                        className="rec-btn-secondary"
                                        onClick={() => setConfirmandoClaveNueva(true)}
                                        disabled={guardando || !clave}
                                    >
                                        <i className="bi bi-arrow-repeat me-1" /> Generar link nuevo
                                    </button>
                                ) : (
                                    <div className="cpi-confirmacion">
                                        <span className="small text-danger">
                                            El TV que esté abierto con el link actual deja de mostrar la pantalla.
                                        </span>
                                        <button type="button" className="rec-btn-primary" onClick={generarClaveNueva} disabled={guardando}>
                                            Confirmar
                                        </button>
                                        <button
                                            type="button"
                                            className="rec-btn-secondary"
                                            onClick={() => setConfirmandoClaveNueva(false)}
                                            disabled={guardando}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                )}
                            </div>

                            <ul className="cpi-instrucciones">
                                <li>
                                    <strong>TV por HDMI (segundo monitor):</strong> "Abrir pantalla" y, en la ventana
                                    nueva, "Mostrar en el TV". La primera vez Chrome pide permiso para administrar ventanas.
                                </li>
                                <li>
                                    <strong>Chromecast:</strong> "Abrir pantalla", "Pantalla completa" y después, en el
                                    menú de Chrome, Transmitir → Transmitir pestaña.
                                </li>
                                <li>
                                    El link no necesita usuario: no lo compartas fuera del estudio. Si se filtró, generá
                                    uno nuevo.
                                </li>
                            </ul>
                        </section>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConfiguracionPantallaInformativaPage;
